import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import CustomerAccountService, {
  parseDashboardAccountsResponse,
} from "../../services/CustomerAccountService";

const PageShell = styled.div`
  font-family: "Inria Serif", Georgia, serif;
  padding-top: var(--page-top-spacing, 8px);
`;

const PageTitle = styled.h1`
  font-family: var(--font-display, "Inria Serif", Georgia, serif);
  font-weight: var(--text-page-title-weight, 600);
  font-size: var(--text-page-title-size, 1.5rem);
  color: var(--foreground, #030213);
  margin: 0 0 8px 0;
  text-align: left;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-family: var(--font-body, Inter, sans-serif);
  font-size: 0.95rem;
  color: var(--muted-foreground, #6b7280);
  margin: 0 0 16px 0;
  line-height: 1.45;
`;

const InfoText = styled.p`
  font-family: "Inria Serif", Georgia, serif;
  font-size: 0.88rem;
  color: #555;
  margin: 0 0 20px 0;
  line-height: 1.4;
`;

const AccountsDashboardContext = createContext(null);

export function useAccountsDashboard() {
  const ctx = useContext(AccountsDashboardContext);
  if (!ctx) {
    throw new Error("useAccountsDashboard must be used within AccountsPageLayout");
  }
  return ctx;
}

const AccountsPageLayout = ({ children }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await CustomerAccountService.getCustomerAccounts();
      const success = resp?.data?.success;
      if (success === false) {
        throw new Error(resp?.data?.message || "Failed to load accounts.");
      }
      const mapped = parseDashboardAccountsResponse(resp);
      setAccounts(mapped);
    } catch (err) {
      console.error(err);
      setAccounts([]);
      setError(err?.response?.data?.message || err?.message || "Failed to load accounts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const upsertAccount = useCallback((account) => {
    setAccounts((prev) => {
      const idx = prev.findIndex(
        (a) => String(a.accountId) === String(account.accountId),
      );
      if (idx === -1) return [...prev, account];
      const next = [...prev];
      next[idx] = { ...next[idx], ...account };
      return next;
    });
  }, []);

  const patchAccountStage = useCallback(async (accountId, stage) => {
    const prev = accounts;
    setAccounts((list) =>
      list.map((a) =>
        String(a.accountId) === String(accountId) ? { ...a, stage } : a,
      ),
    );
    try {
      await CustomerAccountService.updateAccountStage({ accountId, stage });
    } catch (err) {
      console.error(err);
      setAccounts(prev);
      throw err;
    }
  }, [accounts]);

  const value = useMemo(
    () => ({
      accounts,
      setAccounts,
      loading,
      error,
      reload,
      upsertAccount,
      patchAccountStage,
    }),
    [accounts, loading, error, reload, upsertAccount, patchAccountStage],
  );

  return (
    <PageShell>
      <PageTitle>Accounts Dashboard</PageTitle>
      <Subtitle>Manage your business accounts and track their progress</Subtitle>
      <InfoText>
        Cards can be moved between columns to update account stage. Use the pencil icon to open
        details, transactions, and activity in the side panel.
      </InfoText>
      <AccountsDashboardContext.Provider value={value}>
        {children}
      </AccountsDashboardContext.Provider>
    </PageShell>
  );
};

export default AccountsPageLayout;
